import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import {
  CtBinaryOperator,
  CtButtonConfiguration,
  CtCardConfiguration,
  CtCardIconConfiguration,
  CtCardIconPosition,
  CtCardTemplate,
  CtModelConfiguration,
  CTModelDatatableFilter,
  CtModelDatatableOperators,
  CtModelService,
} from '@ctsolution/ct-framework';
import { CompanyType } from '../../../_core/enum/company-type';
import { CtManagerService } from '../../../_core/lib/ct-manager.service';
import { WorkOrderStatusEnum } from '../../../_core/enum/work-order-state';
import { WorkOrderRouteData } from '../../../_core/route-data/work-order.route-data';

@Injectable()
export class DashboardWorkOrderHelper {
  constructor(
    private router: Router,
    private managerService: CtManagerService,
    private modelService: CtModelService<any>
  ) {}
  getWorkOrderWidgets(
    cardList: CtCardConfiguration[],
    counterList: CtCardConfiguration[],
    masterCardList: CtCardConfiguration[]
  ) {
    this.getQuotationBusinessActive(cardList);
    this.getQuotationBusinessInProgress(cardList);
    this.getQuotationBusinessActiveCounter(counterList);
    this.getQuotationBusinessInProgressCounter(counterList);
    this.getWorkOrderActiveCounter(counterList);
    this.getCreateNewQuotation(cardList);
    this.getCreateNewWorkorder(cardList);
    this.getQuotationlist(masterCardList);
    this.getWorkOrderExpiringSoonCard(masterCardList);
  }

  private async getWorkOrderExpiringSoonCard(
    masterCardList: CtCardConfiguration[]
  ) {
    this.managerService
      .getComponentOverride(
        'dashboard',
        'workorder_expiring_soon',
        WORKORDER_EXPIRING_SOON_CARD
      )
      .then((component) => {
        return;
      });

    const now = new Date();
    const threeDaysFromNow = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
    const fiveDaysFromNow = new Date(now.getTime() + 5 * 24 * 60 * 60 * 1000);

    const filters = [
      CTModelDatatableFilter.create()
        .setField('[ExpiredAt] >= ? AND [ExpiredAt] <= ?')
        .setValue([
          threeDaysFromNow.toISOString(),
          fiveDaysFromNow.toISOString(),
        ])
        .setOperatorType(CtBinaryOperator.OrOperator),

      // Solo work order (NON quotations)
      CTModelDatatableFilter.create()
        .setField('Quotation')
        .setValue(false)
        .setOperatorType(CtBinaryOperator.Equal),
    ];

    const result = await this.getWorkOrderByFilterCustom(filters);

    const count = result?.TotalElement ?? 0;
    WORKORDER_EXPIRING_SOON_CARD.setDescription(count.toString());

    const queryObject = {
      constants: [
        {
          Field: '[ExpiredAt] >= ? AND [ExpiredAt] <= ?',
          OperatorType: CtBinaryOperator.OrOperator,
          Value: [
            threeDaysFromNow.toISOString(),
            fiveDaysFromNow.toISOString(),
          ],
        },
        {
          Field: 'Quotation',
          OperatorType: CtBinaryOperator.Equal,
          Value: false,
        },
      ],
    };

    const queryString = JSON.stringify(queryObject);
    const urlTree: UrlTree = this.router.createUrlTree(['/work-order/list'], {
      queryParams: { q: queryString },
    });
    const fullUrl = this.router.serializeUrl(urlTree);

    masterCardList.push(
      WORKORDER_EXPIRING_SOON_CARD.setAction(() =>
        window.open(fullUrl, '_blank')
      )
    );
  }

  private async getWorkOrderByFilterCustom(
    filters: Array<CTModelDatatableFilter>
  ) {
    const configuration: CtModelConfiguration<any> =
      CtModelConfiguration.create().setRouteData(WorkOrderRouteData());

    const operators: CtModelDatatableOperators =
      CtModelDatatableOperators.create().setFilters(filters);

    return new Promise<CtModelConfiguration<Array<any>>>((resolve) => {
      this.modelService
        .getList(configuration, operators)
        .subscribe((response) => resolve(response.Result));
    });
  }

  private getCreateNewQuotation(cardList: CtCardConfiguration[]) {
    this.managerService
      .getComponentOverride(
        'dashboard',
        'create_new_quotation',
        NEW_QUOTATION_CARD
      )
      .then((component) => {
        return;
      });

    cardList.push(
      NEW_QUOTATION_CARD.setAction(() =>
        this.router.navigate(['/', 'work-order', 'quotations', 'create'])
      )
    );
  }
  private getQuotationlist(cardList: CtCardConfiguration[]) {
    this.managerService
      .getComponentOverride('dashboard', 'quotation_list', QUOTATION_CARD)
      .then((component) => {
        return;
      });

    cardList.push(
      QUOTATION_CARD.setAction(() =>
        this.router.navigate(['/', 'work-order', 'quotations'])
      )
    );
  }

  private getCreateNewWorkorder(cardList: CtCardConfiguration[]) {
    this.managerService
      .getComponentOverride(
        'dashboard',
        'create_new_workorder',
        NEW_WORKORDER_CARD
      )
      .then((component) => {
        return;
      });

    cardList.push(
      NEW_WORKORDER_CARD.setAction(() =>
        this.router.navigate(['/', 'work-order', 'create'])
      )
    );
  }

  private getQuotationBusinessActive(cardList: CtCardConfiguration[]) {
    this.managerService
      .getComponentOverride(
        'dashboard',
        'quotation_business_list_active',
        QUOTATION_BUSINESS_LIST_ACTIVE_CARD
      )
      .then((component) => {
        return;
      });

    const fullUrl = this.getUrlQuotationBusinessbyStatus(
      WorkOrderStatusEnum.Active
    );

    cardList.push(
      QUOTATION_BUSINESS_LIST_ACTIVE_CARD.setAction(() =>
        window.open(fullUrl, '_blank')
      )
    );
  }

  private getQuotationBusinessInProgress(cardList: CtCardConfiguration[]) {
    this.managerService
      .getComponentOverride(
        'dashboard',
        'quotation_business_list_inProgress',
        QUOTATION_BUSINESS_LIST_INPROGRESS_CARD
      )
      .then((component) => {
        return;
      });

    const fullUrl = this.getUrlQuotationBusinessbyStatus(
      WorkOrderStatusEnum.InProgress
    );

    cardList.push(
      QUOTATION_BUSINESS_LIST_INPROGRESS_CARD.setAction(() =>
        window.open(fullUrl, '_blank')
      )
    );
  }

  private getUrlQuotationBusinessbyStatus(status: WorkOrderStatusEnum) {
    const queryObject = {
      constants: [
        {
          Field: '[Company.Type.Type] is not null AND [Company.Type.Type] = ?',
          OperatorType: CtBinaryOperator.OrOperator,
          Value: [CompanyType.Business],
        },
        {
          Field: 'State.Type',
          OperatorType: CtBinaryOperator.Equal,
          Value: status,
        },
      ],
    };

    const queryString = JSON.stringify(queryObject);
    const urlTree: UrlTree = this.router.createUrlTree(
      ['/work-order/quotations/list'],
      { queryParams: { q: queryString } }
    );

    // Convertiamo l'UrlTree in una stringa e apriamo una nuova scheda
    const fullUrl = this.router.serializeUrl(urlTree);
    return fullUrl;
  }

  private getUrlWorkOrderbyStatus(status: WorkOrderStatusEnum) {
    const queryObject = {
      constants: [
        {
          Field: 'State.Type',
          OperatorType: CtBinaryOperator.Equal,
          Value: status,
        },
      ],
    };

    const queryString = JSON.stringify(queryObject);
    const urlTree: UrlTree = this.router.createUrlTree(['/work-order/list'], {
      queryParams: { q: queryString },
    });

    // Convertiamo l'UrlTree in una stringa e apriamo una nuova scheda
    const fullUrl = this.router.serializeUrl(urlTree);
    return fullUrl;
  }

  async getQuotationBusinessActiveCounter(counterList: CtCardConfiguration[]) {
    this.managerService
      .getComponentOverride(
        'dashboard',
        'quotation_business_counter_active',
        QUOTATION_BUSINESS_COUNTER_ACTIVE_CARD
      )
      .then((component) => {
        return;
      });

    const resultProspect = await this.getQuotationByFilter(
      WorkOrderStatusEnum.Active
    );

    const fullUrl = this.getUrlQuotationBusinessbyStatus(
      WorkOrderStatusEnum.Active
    );

    QUOTATION_BUSINESS_COUNTER_ACTIVE_CARD.setAction(() =>
      window.open(fullUrl, '_blank')
    );

    this.setupCounter(
      QUOTATION_BUSINESS_COUNTER_ACTIVE_CARD,
      resultProspect,
      counterList
    );
  }

  async getWorkOrderActiveCounter(counterList: CtCardConfiguration[]) {
    this.managerService
      .getComponentOverride(
        'dashboard',
        'workorder_counter_active',
        WORKORDER_COUNTER_ACTIVE_CARD
      )
      .then((component) => {
        return;
      });

    const resultProspect = await this.getWorkOrderByFilter(
      WorkOrderStatusEnum.Active
    );

    const fullUrl = this.getUrlWorkOrderbyStatus(WorkOrderStatusEnum.Active);

    WORKORDER_COUNTER_ACTIVE_CARD.setAction(() =>
      window.open(fullUrl, '_blank')
    );

    this.setupCounter(
      WORKORDER_COUNTER_ACTIVE_CARD,
      resultProspect,
      counterList
    );
  }

  async getQuotationBusinessInProgressCounter(
    counterList: CtCardConfiguration[]
  ) {
    this.managerService
      .getComponentOverride(
        'dashboard',
        'quotation_business_counter_inProgress',
        QUOTATION_BUSINESS_COUNTER_INPROGRESS_CARD
      )
      .then((component) => {
        return;
      });

    const resultProspect = await this.getQuotationByFilter(
      WorkOrderStatusEnum.InProgress
    );

    const fullUrl = this.getUrlQuotationBusinessbyStatus(
      WorkOrderStatusEnum.InProgress
    );

    QUOTATION_BUSINESS_COUNTER_INPROGRESS_CARD.setAction(() =>
      window.open(fullUrl, '_blank')
    );

    this.setupCounter(
      QUOTATION_BUSINESS_COUNTER_INPROGRESS_CARD,
      resultProspect,
      counterList
    );
  }

  async getQuotationByFilter(
    state: WorkOrderStatusEnum,
    companyType: CompanyType = CompanyType.Business
  ) {
    const configuration: CtModelConfiguration<any> =
      CtModelConfiguration.create().setRouteData(WorkOrderRouteData());

    const operators: CtModelDatatableOperators =
      CtModelDatatableOperators.create().setFilters([
        CTModelDatatableFilter.create()
          .setField('State.Type')
          .setValue(state)
          .setOperatorType(CtBinaryOperator.Equal),

        CTModelDatatableFilter.create()
          .setField(
            '[Company.Type.Type] is not null AND [Company.Type.Type] = ?'
          )
          .setValue([companyType])
          .setOperatorType(CtBinaryOperator.OrOperator),

        CTModelDatatableFilter.create()
          .setField('Quotation')
          .setValue(true)
          .setOperatorType(CtBinaryOperator.Equal),
      ]);

    return new Promise<CtModelConfiguration<Array<any>>>((resolve) => {
      this.modelService
        .getList(configuration, operators)
        .subscribe((response) => resolve(response.Result));
    });
  }

  async getWorkOrderByFilter(
    state: WorkOrderStatusEnum,
    companyType: CompanyType = CompanyType.Business
  ) {
    const configuration: CtModelConfiguration<any> =
      CtModelConfiguration.create().setRouteData(WorkOrderRouteData());

    const operators: CtModelDatatableOperators =
      CtModelDatatableOperators.create().setFilters([
        CTModelDatatableFilter.create()
          .setField('State.Type')
          .setValue(state)
          .setOperatorType(CtBinaryOperator.Equal),

        CTModelDatatableFilter.create()
          .setField('Quotation')
          .setValue(false)
          .setOperatorType(CtBinaryOperator.Equal),
      ]);

    return new Promise<CtModelConfiguration<Array<any>>>((resolve) => {
      this.modelService
        .getList(configuration, operators)
        .subscribe((response) => resolve(response.Result));
    });
  }

  private setupCounter(
    counter: CtCardConfiguration,
    result: CtModelConfiguration<any>,
    counterList: CtCardConfiguration[]
  ) {
    counter.setDescription(
      (result?.TotalElement ?? 0) > 0 ? result.TotalElement : '0'
    );

    counterList.push(counter);
  }
}

export const QUOTATION_BUSINESS_LIST_ACTIVE_CARD: CtCardConfiguration =
  CtCardConfiguration.create()
    .setTitle('dashboard.quotationActive')
    .setVisible(false)
    .setDescription('dashboard.transitToQuotationActiveList')
    .setIconSetting(
      CtCardIconConfiguration.create()
        .setClass('bg-ct-manager-alt text-white')
        .setIcon('build')
    );

export const QUOTATION_CARD: CtCardConfiguration = CtCardConfiguration.create()
  .setTitle('dashboard.workorderList')
  .setVisible(false)
  .setDescription('dashboard.workorderListDescription')
  .setIconSetting(
    CtCardIconConfiguration.create()
      .setIcon('av_timer')
      .setPosition(CtCardIconPosition.SUFFIX)
      .setClass("text-ct-manager bg-white")
  )
  .setClass('bg-ct-manager text-white')
  .setButton(CtButtonConfiguration.create().setIcon('arrow_forward'));



export const QUOTATION_BUSINESS_LIST_INPROGRESS_CARD: CtCardConfiguration =
  CtCardConfiguration.create()
    .setTitle('dashboard.quotationInProgress')
    .setVisible(false)
    .setDescription('dashboard.transitToQuotationInProgressList')
    .setIconSetting(
      CtCardIconConfiguration.create()
        .setClass('bg-ct-manager-alt text-white')
        .setIcon('build')
    );

export const QUOTATION_BUSINESS_COUNTER_ACTIVE_CARD: CtCardConfiguration =
  CtCardConfiguration.create()
    .setTitle('dashboard.quotationActiveCounter')
    .setVisible(false)
    .setTemplate(CtCardTemplate.COUNTER)
    .setClass('bg-warning text-white');

export const WORKORDER_COUNTER_ACTIVE_CARD: CtCardConfiguration =
  CtCardConfiguration.create()
    .setTitle('dashboard.workorderActiveCounter')
    .setVisible(false)
    .setTemplate(CtCardTemplate.COUNTER)
    .setClass('bg-warning text-white');

export const QUOTATION_BUSINESS_COUNTER_INPROGRESS_CARD: CtCardConfiguration =
  CtCardConfiguration.create()
    .setTitle('dashboard.quotationInProgressCounter')
    .setVisible(false)
    .setTemplate(CtCardTemplate.COUNTER)
    .setClass('bg-warning text-white');

export const NEW_QUOTATION_CARD: CtCardConfiguration =
  CtCardConfiguration.create()
    .setTitle('dashboard.createNewQuotation')
    .setVisible(false)
    .setDescription('dashboard.createNewQuotationDescription')
    .setIconSetting(
      CtCardIconConfiguration.create()
        .setClass('bg-danger text-white')
        .setIcon('account_balance')
    );

export const NEW_WORKORDER_CARD: CtCardConfiguration =
  CtCardConfiguration.create()
    .setTitle('dashboard.createNewWorkorder')
    .setVisible(false)
    .setDescription('dashboard.createNewWorkorderDescription')
    .setIconSetting(
      CtCardIconConfiguration.create()
        .setClass('bg-danger text-white')
        .setIcon('post_add')
    );

export const WORKORDER_EXPIRING_SOON_CARD: CtCardConfiguration = CtCardConfiguration.create()

    .setTitle('dashboard.workorderExpiringSoon')
    .setVisible(false)
    .setIconSetting(
      CtCardIconConfiguration.create()
        .setIcon('local_shipping')
        .setPosition(CtCardIconPosition.SUFFIX)
        .setClass("bg-white text-primary")
    )
    .setClass('bg-primary text-white')
    .setButton(CtButtonConfiguration.create().setIcon('arrow_forward'));

